import type { CanvasBuilderElement } from "./canvasBuilderProjection";

/** Return the first row below every child in the target parent/panel scope. */
export function firstFreeNestedChildRow(
  elements: CanvasBuilderElement[],
  parentId: string,
  panelId?: string,
  childRowSpan = 1,
  maxRows = Number.POSITIVE_INFINITY,
): number | null {
  const nextRow = elements
    .filter(
      (element) =>
        element.parentElementId === parentId &&
        element.parentPanelId === panelId,
    )
    .reduce(
      (nextRow, element) =>
        Math.max(nextRow, element.gridRow + element.rowSpan),
      1,
    );
  return nextRow + childRowSpan - 1 <= maxRows ? nextRow : null;
}

/** Find a valid append row when moving an existing element into a container. */
export function existingNestedChildDropRow(
  elements: CanvasBuilderElement[],
  childId: string,
  parentId: string,
  panelId?: string,
): number | null {
  const child = elements.find((element) => element.tempId === childId);
  const parent = elements.find((element) => element.tempId === parentId);
  if (!child || !parent || childId === parentId) return null;

  return firstFreeNestedChildRow(
    elements.filter((element) => element.tempId !== childId),
    parentId,
    panelId,
    child.rowSpan,
    parent.rowSpan,
  );
}

export interface CanvasBuilderDrop {
  parentId?: string;
  panelId?: string;
  index: number;
  row: number;
  column: number;
}
/** Insert into the existing draft and renumber both affected sibling collections. */
export function insertCanvasBuilderElement(
  elements: CanvasBuilderElement[],
  id: string,
  drop: CanvasBuilderDrop,
): CanvasBuilderElement[] {
  const item = elements.find((element) => element.tempId === id);
  if (!item) return elements;
  const sameParent = (
    element: CanvasBuilderElement,
    parentId?: string,
    panelId?: string,
  ) =>
    element.parentElementId === parentId && element.parentPanelId === panelId;
  const byOrder = (a: CanvasBuilderElement, b: CanvasBuilderElement) =>
    (a.displayOrder || 0) - (b.displayOrder || 0);
  const remaining = elements.filter((element) => element.tempId !== id);
  const siblings = remaining
    .filter((element) => sameParent(element, drop.parentId, drop.panelId))
    .sort(byOrder);
  const moved = {
    ...item,
    parentElementId: drop.parentId,
    parentPanelId: drop.panelId,
    gridRow: drop.row,
    gridColumn: drop.column,
  };
  siblings.splice(Math.max(0, Math.min(drop.index, siblings.length)), 0, moved);
  const updates = new Map<string, CanvasBuilderElement>();
  remaining
    .filter((element) =>
      sameParent(element, item.parentElementId, item.parentPanelId),
    )
    .sort(byOrder)
    .forEach((element, index) =>
      updates.set(element.tempId, { ...element, displayOrder: index }),
    );
  siblings.forEach((element, index) =>
    updates.set(element.tempId, { ...element, displayOrder: index }),
  );
  return elements.map((element) => updates.get(element.tempId) || element);
}

/** Place a pointer drop before the following sibling in a grid row or stack. */
export function isDropBeforeSibling(
  point: { x: number; y: number },
  rect: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    height: number;
  },
): boolean {
  if (point.y < rect.top) return true;
  if (point.y > rect.bottom) return false;
  if (point.x < rect.left) return true;
  if (point.x > rect.right) return false;
  return point.y < rect.top + rect.height / 2;
}
